import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, DollarSign, Package, Truck, Clock, FileText, Camera, MapPin, Search, ChevronDown, X, Eye, ClipboardList, Receipt, FileCheck, HardHat, Share2, ExternalLink, Users } from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';
import { Job } from '../../types/backend.types';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { geofenceService } from '../../services/geofence.service';

// LocalStorage key and cache duration (12 hours in ms)
const STORAGE_KEY = 'jobActivity_selectedJob';
const CACHE_DURATION = 12 * 60 * 60 * 1000;

interface CachedJob {
  jobId: number;
  timestamp: number;
}

interface JobActivityStats {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  // Material imports
  fill_loads: number;
  rock_loads: number;
  baserock_loads: number;
  asphalt_loads: number;
  other_loads: number;
  // Material exports (haul-off)
  fill_dirt_truck: number;
  baserock_truck: number;
  asphalt_truck: number;
  asphalt_millings_truck: number;
  concrete_truck: number;
  misc_truck: number;
  concrete_dumpster: number;
  demolition_dumpster: number;
  vegetation_dumpster: number;
  // Labor
  estimated_labor_cost: number;
  foreman_hours: number;
  field_worker_hours: number;
  truck_driver_hours: number;
  total_hours: number;
  // Counts
  daily_reports_count?: number;
  tm_tickets_count?: number;
  truck_tickets_count?: number;
  days_on_job?: number;
}

export const JobActivity: React.FC = () => {
  const navigate = useNavigate();
  const { forceRefreshLocation } = useLocation();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [jobStats, setJobStats] = useState<JobActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'labor' | 'materials'>('overview');

  // Searchable dropdown state
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Geofence toggle state
  const [geofenceEnabled, setGeofenceEnabled] = useState(true);
  const [showGeofenceConfirm, setShowGeofenceConfirm] = useState(false);

  // Closest job modal state
  const [showClosestJobModal, setShowClosestJobModal] = useState(false);
  const [closestJob, setClosestJob] = useState<{ job: Job; distance: number } | null>(null);

  // Save job to localStorage
  const saveJobToStorage = (jobId: number) => {
    const cached: CachedJob = { jobId, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
  };

  // Load cached job from localStorage
  const loadCachedJob = (jobList: Job[]): Job | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const cached: CachedJob = JSON.parse(stored);
      // Check if cache is still valid (within 12 hours)
      if (Date.now() - cached.timestamp > CACHE_DURATION) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      // Find the job in the list
      return jobList.find(j => j.job_id === cached.jobId) || null;
    } catch {
      return null;
    }
  };

  // Clear cached job
  const clearCachedJob = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  // Load jobs on mount
  useEffect(() => {
    const loadJobs = async () => {
      try {
        setLoading(true);
        const response = await api.get(API_CONFIG.ENDPOINTS.JOBS);
        if (response.data.success) {
          // Sort jobs by name
          const sortedJobs = response.data.data.sort((a: Job, b: Job) =>
            a.job_name.localeCompare(b.job_name)
          );
          setJobs(sortedJobs);

          // Load cached job if available
          const cachedJob = loadCachedJob(sortedJobs);
          if (cachedJob) {
            setCurrentJob(cachedJob);
          }
        }
      } catch (err) {
        console.error('Error loading jobs:', err);
        setError('Failed to load jobs');
      } finally {
        setLoading(false);
      }
    };

    loadJobs();
  }, []);

  // Check if user is within job's geofence
  const isUserWithinJobGeofence = useCallback((job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      return geofenceService.isInsideCircle(
        userLat,
        userLng,
        job.lat,
        job.lng,
        job.geofence_data.radius
      );
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      return geofenceService.isInsidePolygon(
        userLat,
        userLng,
        job.geofence_data.points
      );
    }

    // Default to 100m radius if no geofence defined
    const distance = geofenceService.calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  }, []);

  // Select job and save to storage
  const selectJob = (job: Job | null) => {
    setCurrentJob(job);
    if (job) {
      saveJobToStorage(job.job_id);
    } else {
      clearCachedJob();
    }
  };

  // Find location button handler
  const handleFindLocation = async () => {
    try {
      setLoadingLocation(true);
      setError(null);
      const loc = await forceRefreshLocation();

      if (!loc?.latitude || !loc?.longitude) {
        setError('Could not get your location. Please try again.');
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Find job user is at
      for (const job of jobs) {
        if (!job.lat || !job.lng) continue;
        if (isUserWithinJobGeofence(job, loc.latitude, loc.longitude)) {
          selectJob(job);
          setSearchTerm('');
          setIsDropdownOpen(false);
          return;
        }
      }

      // No job found at location - find closest job
      let nearest: { job: Job; distance: number } | null = null;
      for (const job of jobs) {
        if (!job.lat || !job.lng) continue;
        const distance = geofenceService.calculateDistance(
          loc.latitude,
          loc.longitude,
          job.lat,
          job.lng
        );
        if (!nearest || distance < nearest.distance) {
          nearest = { job, distance };
        }
      }

      if (nearest) {
        setClosestJob(nearest);
        setShowClosestJobModal(true);
      } else {
        setError('No jobs available');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Error getting location:', err);
      setError('Failed to get location');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingLocation(false);
    }
  };

  // Format distance for display
  const formatDistance = (meters: number): string => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    const miles = meters / 1609.34;
    return `${miles.toFixed(1)} mi`;
  };

  // Build full address string
  const getFullAddress = (job: Job): string => {
    const parts = [];
    if (job.address) parts.push(job.address);
    if (job.city) parts.push(job.city);
    if (job.state) parts.push(job.state);
    if (job.zip_code) parts.push(job.zip_code);
    return parts.join(', ');
  };

  // Open address in maps app
  const openInMaps = () => {
    if (!currentJob) return;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /android/.test(userAgent);

    if (currentJob.address) {
      const address = encodeURIComponent(getFullAddress(currentJob));
      if (isIOS) {
        window.location.href = `maps://?q=${address}`;
      } else if (isAndroid) {
        window.location.href = `geo:0,0?q=${address}`;
      } else {
        window.open(`https://maps.google.com/?q=${address}`, '_blank');
      }
    } else if (currentJob.lat && currentJob.lng) {
      if (isIOS) {
        window.location.href = `maps://?q=${encodeURIComponent(currentJob.job_name)}&ll=${currentJob.lat},${currentJob.lng}`;
      } else if (isAndroid) {
        window.location.href = `geo:${currentJob.lat},${currentJob.lng}?q=${currentJob.lat},${currentJob.lng}(${encodeURIComponent(currentJob.job_name)})`;
      } else {
        window.open(`https://maps.google.com/?q=${currentJob.lat},${currentJob.lng}`, '_blank');
      }
    }
  };

  // Share address using Web Share API or clipboard fallback
  const shareAddress = async () => {
    if (!currentJob) return;
    const fullAddress = getFullAddress(currentJob);
    const mapsUrl = currentJob.address
      ? `https://maps.apple.com/?q=${encodeURIComponent(fullAddress)}`
      : (currentJob.lat && currentJob.lng ? `https://maps.apple.com/?ll=${currentJob.lat},${currentJob.lng}&q=${encodeURIComponent(currentJob.job_name)}` : '');

    const shareText = [`📍 ${currentJob.job_name}`, '', fullAddress, '', mapsUrl].join('\n').trim();

    if (navigator.share) {
      try {
        await navigator.share({ title: currentJob.job_name, text: shareText });
        return;
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      alert('Address copied to clipboard');
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  // Handle selecting closest job from modal
  const handleSelectClosestJob = () => {
    if (closestJob) {
      selectJob(closestJob.job);
      setSearchTerm('');
      setIsDropdownOpen(false);
    }
    setShowClosestJobModal(false);
    setClosestJob(null);
  };

  // Filter jobs based on search
  const filteredJobs = jobs.filter(job =>
    job.job_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (job.bradanna_job_number && job.bradanna_job_number.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sync geofence toggle with current job
  useEffect(() => {
    if (currentJob) {
      setGeofenceEnabled(currentJob.geofence_enabled !== false);
    }
  }, [currentJob?.job_id, currentJob?.geofence_enabled]);

  // Fetch job stats when job changes
  useEffect(() => {
    const fetchJobStats = async () => {
      if (!currentJob?.job_id) {
        setJobStats(null);
        return;
      }

      try {
        setLoadingStats(true);
        const response = await api.get(API_CONFIG.ENDPOINTS.JOB_ACTIVITY_STATS(currentJob.job_id));
        if (response.data.success) {
          setJobStats(response.data.data);
        } else {
          // If endpoint not available yet, set empty stats
          setJobStats(null);
        }
      } catch (err) {
        console.error('Error fetching job stats:', err);
        // Don't show error - stats endpoint may not exist yet
        setJobStats(null);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchJobStats();
  }, [currentJob?.job_id]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format hours
  const formatHours = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // Calculate total haul-off loads
  const getTotalHaulOff = (): number => {
    if (!jobStats) return 0;
    return (
      (jobStats.fill_dirt_truck || 0) +
      (jobStats.baserock_truck || 0) +
      (jobStats.asphalt_truck || 0) +
      (jobStats.asphalt_millings_truck || 0) +
      (jobStats.concrete_truck || 0) +
      (jobStats.misc_truck || 0) +
      (jobStats.concrete_dumpster || 0) +
      (jobStats.demolition_dumpster || 0) +
      (jobStats.vegetation_dumpster || 0)
    );
  };

  // Calculate total material imports
  const getTotalImports = (): number => {
    if (!jobStats) return 0;
    return (
      (jobStats.fill_loads || 0) +
      (jobStats.rock_loads || 0) +
      (jobStats.baserock_loads || 0) +
      (jobStats.asphalt_loads || 0) +
      (jobStats.other_loads || 0)
    );
  };

  // Handle geofence toggle confirm
  const handleGeofenceToggle = async () => {
    if (!currentJob) return;
    try {
      const newValue = !geofenceEnabled;
      await api.put(`${API_CONFIG.ENDPOINTS.JOBS}/${currentJob.job_id}`, { geofence_enabled: newValue });
      setGeofenceEnabled(newValue);
      // Update the job in the jobs array
      setJobs(prev => prev.map(j => j.job_id === currentJob.job_id ? { ...j, geofence_enabled: newValue } : j));
      setCurrentJob(prev => prev ? { ...prev, geofence_enabled: newValue } : prev);
    } catch (err) {
      console.error('Failed to toggle geofence:', err);
      alert('Failed to update clock-in location setting');
    }
    setShowGeofenceConfirm(false);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: '#3b82f6',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: '#64748b',
          fontSize: 16,
          marginTop: 16,
          fontWeight: '500'
        }}>
          Loading...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header - styled like CDL Driver Tracking */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
            Job Activity
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            View statistics and navigate to modules
          </p>
        </div>
      </div>

      <div style={{ padding: '16px' }}>

      {/* Job Selector with Search and Location Button */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Searchable Dropdown */}
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                border: isDropdownOpen ? '2px solid #3b82f6' : '2px solid transparent'
              }}
            >
              {currentJob ? (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentJob.job_name}
                  </div>
                  {currentJob.bradanna_job_number && (
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {currentJob.bradanna_job_number}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ flex: 1, color: '#64748b', fontSize: '15px' }}>
                  Select a job...
                </div>
              )}
              {currentJob ? (
                <X
                  size={20}
                  color="#94a3b8"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectJob(null);
                    setSearchTerm('');
                  }}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <ChevronDown size={20} color="#94a3b8" />
              )}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                zIndex: 100,
                maxHeight: '300px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Search Input */}
                <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}>
                    <Search size={18} color="#64748b" />
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      style={{
                        flex: 1,
                        border: 'none',
                        backgroundColor: 'transparent',
                        fontSize: '15px',
                        color: '#1e293b',
                        outline: 'none'
                      }}
                    />
                    {searchTerm && (
                      <X
                        size={18}
                        color="#94a3b8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchTerm('');
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </div>
                </div>

                {/* Job List */}
                <div style={{ overflowY: 'auto', maxHeight: '240px' }}>
                  {filteredJobs.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#64748b' }}>
                      No jobs found
                    </div>
                  ) : (
                    filteredJobs.map((job) => (
                      <div
                        key={job.job_id}
                        onClick={() => {
                          selectJob(job);
                          setSearchTerm('');
                          setIsDropdownOpen(false);
                        }}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: currentJob?.job_id === job.job_id ? '#eff6ff' : 'transparent'
                        }}
                      >
                        <div style={{ fontWeight: '500', color: '#1e293b', fontSize: '14px' }}>
                          {job.job_name}
                        </div>
                        {job.bradanna_job_number && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            {job.bradanna_job_number}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Find Location Button */}
          <button
            onClick={handleFindLocation}
            disabled={loadingLocation}
            style={{
              backgroundColor: '#3b82f6',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 16px',
              cursor: loadingLocation ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              opacity: loadingLocation ? 0.7 : 1
            }}
          >
            {loadingLocation ? (
              <Loader2 size={22} color="white" style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <MapPin size={22} color="white" />
            )}
          </button>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isDropdownOpen && (
        <div
          onClick={() => setIsDropdownOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99
          }}
        />
      )}

      {/* Address Card - show when job is selected and has address or coordinates */}
      {currentJob && (currentJob.address || (currentJob.lat && currentJob.lng)) && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '14px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginTop: '12px'
        }}>
          {/* Address text */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
            <MapPin size={16} color="#16a34a" style={{ marginTop: '2px', flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.4' }}>
              {currentJob.address
                ? `${currentJob.address}, ${currentJob.city || ''}${currentJob.state ? `, ${currentJob.state}` : ''}${currentJob.zip_code ? ` ${currentJob.zip_code}` : ''}`
                : `${currentJob.lat.toFixed(5)}, ${currentJob.lng.toFixed(5)}`
              }
            </span>
          </div>
          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={openInMaps}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                color: '#16a34a'
              }}
            >
              <ExternalLink size={15} />
              Open in Maps
            </button>
            {currentJob.address && (
              <button
                onClick={shareAddress}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#3b82f6'
                }}
              >
                <Share2 size={15} />
                Share Address
              </button>
            )}
          </div>
        </div>
      )}

      {/* Geofence Toggle - show when job is selected */}
      {currentJob && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MapPin size={16} color={geofenceEnabled ? '#16a34a' : '#94a3b8'} />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
              Clock-In Location
            </span>
          </div>
          <div
            onClick={() => setShowGeofenceConfirm(true)}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              backgroundColor: geofenceEnabled ? '#16a34a' : '#d1d5db',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background-color 0.2s'
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: 'white',
              position: 'absolute',
              top: '2px',
              left: geofenceEnabled ? '22px' : '2px',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }} />
          </div>
        </div>
      )}

      {/* Geofence Confirm Modal */}
      {showGeofenceConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '340px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' }}>
              {geofenceEnabled ? 'Disable' : 'Enable'} Clock-In Location?
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0', lineHeight: '1.5' }}>
              {geofenceEnabled
                ? 'Workers will not be able to see or clock into this job from the field.'
                : 'Workers will be able to see and clock into this job from the field again.'}
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowGeofenceConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#64748b',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGeofenceToggle}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: geofenceEnabled ? '#ef4444' : '#16a34a',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                {geofenceEnabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Section - only show when job is selected */}
      {currentJob && (
        <>
          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: '#f1f5f9',
            padding: '4px',
            borderRadius: '10px',
            marginTop: '16px'
          }}>
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'labor', label: 'Labor' },
              { key: 'materials', label: 'Materials' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: activeTab === tab.key ? 'white' : 'transparent',
                  color: activeTab === tab.key ? '#1e293b' : '#64748b',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: activeTab === tab.key ? '600' : '500',
                  cursor: 'pointer',
                  boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={{ marginTop: '16px' }}>
              {/* Quick Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <StatCard
                  icon={Clock}
                  label="Total Hours"
                  value={loadingStats ? '-' : formatHours(jobStats?.total_hours || 0)}
                  color="#4f46e5"
                />
                <StatCard
                  icon={DollarSign}
                  label="Labor Cost"
                  value={loadingStats ? '-' : formatCurrency(jobStats?.estimated_labor_cost || 0)}
                  color="#10b981"
                />
                <StatCard
                  icon={Truck}
                  label="Material In"
                  value={loadingStats ? '-' : getTotalImports().toString()}
                  color="#f59e0b"
                />
                <StatCard
                  icon={Package}
                  label="Haul Off"
                  value={loadingStats ? '-' : getTotalHaulOff().toString()}
                  color="#ef4444"
                />
              </div>

              {/* Viewers Section */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '12px'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  margin: '0 0 12px 0'
                }}>
                  Viewers
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <ModuleButton
                    icon={ClipboardList}
                    label="Report Viewer"
                    color="#059669"
                    onClick={() => navigate('/daily-report-viewer', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={Eye}
                    label="Ticket Viewer"
                    color="#8b5cf6"
                    onClick={() => navigate('/truck-ticket-viewer', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={Receipt}
                    label="T&M Viewer"
                    color="#db2777"
                    onClick={() => navigate('/tm-ticket-viewer', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={Camera}
                    label="Photos"
                    color="#f59e0b"
                    onClick={() => navigate('/photos', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={FileCheck}
                    label="Startup Sheet"
                    color="#0ea5e9"
                    onClick={() => navigate('/startup-sheet', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={FileText}
                    label="Scope of Work"
                    color="#f59e0b"
                    onClick={() => navigate('/job-scope-of-work', { state: { jobId: currentJob.job_id } })}
                  />
                  {currentJob.bradanna_job_number && (
                    <ModuleButton
                      icon={FileText}
                      label="View Plans"
                      color="#7c3aed"
                      onClick={() => navigate('/plans', { state: { jobName: currentJob.job_name, jobNumber: currentJob.bradanna_job_number } })}
                    />
                  )}
                  <ModuleButton
                    icon={Users}
                    label="Subcontractors"
                    color="#14b8a6"
                    onClick={() => navigate('/subcontractor-viewer', { state: { jobId: currentJob.job_id } })}
                  />
                </div>
              </div>

              {/* Actions Section */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  margin: '0 0 12px 0'
                }}>
                  Actions
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <ModuleButton
                    icon={FileText}
                    label="Daily Reports"
                    color="#10b981"
                    onClick={() => navigate('/daily-reports', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={Truck}
                    label="Truck Tickets"
                    color="#6366f1"
                    onClick={() => navigate('/trucks', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={Package}
                    label="T&M Tickets"
                    color="#ec4899"
                    onClick={() => navigate('/tm-tickets', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={HardHat}
                    label="Subcontractors"
                    color="#ef4444"
                    onClick={() => navigate('/job-subcontractors', { state: { jobId: currentJob.job_id } })}
                  />
                  <ModuleButton
                    icon={ClipboardList}
                    label="Job Requirements"
                    color="#8b5cf6"
                    onClick={() => navigate('/special-requirements', { state: { jobId: currentJob.job_id, returnPath: '/job-activity' } })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Labor Tab */}
          {activeTab === 'labor' && (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '12px'
              }}>
                <LaborRow label="Foreman Hours" hours={jobStats?.foreman_hours || 0} color="#4f46e5" loading={loadingStats} />
                <LaborRow label="Field Worker Hours" hours={jobStats?.field_worker_hours || 0} color="#06b6d4" loading={loadingStats} />
                <LaborRow label="Truck Driver Hours" hours={jobStats?.truck_driver_hours || 0} color="#10b981" loading={loadingStats} />
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: '#f0f9ff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Total Hours</span>
                  <span style={{ fontWeight: '700', fontSize: '18px', color: '#4f46e5' }}>
                    {loadingStats ? '-' : formatHours(jobStats?.total_hours || 0)}
                  </span>
                </div>
              </div>

              <div style={{
                backgroundColor: '#10b981',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', margin: '0 0 4px' }}>
                  Estimated Labor Cost
                </p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: 'white', margin: 0 }}>
                  {loadingStats ? '-' : formatCurrency(jobStats?.estimated_labor_cost || 0)}
                </p>
              </div>
            </div>
          )}

          {/* Materials Tab */}
          {activeTab === 'materials' && (
            <div style={{ marginTop: '16px' }}>
              {/* Material Imports */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '12px'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748b',
                  margin: 0,
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  backgroundColor: '#f8fafc'
                }}>
                  Material Imported
                </h3>
                <MaterialRow label="Fill" count={jobStats?.fill_loads || 0} loading={loadingStats} />
                <MaterialRow label="Rock" count={jobStats?.rock_loads || 0} loading={loadingStats} />
                <MaterialRow label="Base Rock" count={jobStats?.baserock_loads || 0} loading={loadingStats} />
                <MaterialRow label="Asphalt" count={jobStats?.asphalt_loads || 0} loading={loadingStats} />
                <MaterialRow label="Other" count={jobStats?.other_loads || 0} loading={loadingStats} />
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f0fdf4',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Total Loads In</span>
                  <span style={{ fontWeight: '700', color: '#10b981' }}>
                    {loadingStats ? '-' : getTotalImports()}
                  </span>
                </div>
              </div>

              {/* Material Haul-Off - Individual Items */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748b',
                  margin: 0,
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  backgroundColor: '#fff7ed'
                }}>
                  Material Hauled Off
                </h3>
                <MaterialRow label="Fill Dirt (Truck)" count={jobStats?.fill_dirt_truck || 0} loading={loadingStats} />
                <MaterialRow label="Base Rock (Truck)" count={jobStats?.baserock_truck || 0} loading={loadingStats} />
                <MaterialRow label="Asphalt (Truck)" count={jobStats?.asphalt_truck || 0} loading={loadingStats} />
                <MaterialRow label="Asphalt Millings (Truck)" count={jobStats?.asphalt_millings_truck || 0} loading={loadingStats} />
                <MaterialRow label="Concrete (Truck)" count={jobStats?.concrete_truck || 0} loading={loadingStats} />
                <MaterialRow label="Misc (Truck)" count={jobStats?.misc_truck || 0} loading={loadingStats} />
                <MaterialRow label="Concrete (Dumpster)" count={jobStats?.concrete_dumpster || 0} loading={loadingStats} />
                <MaterialRow label="Demolition (Dumpster)" count={jobStats?.demolition_dumpster || 0} loading={loadingStats} />
                <MaterialRow label="Vegetation (Dumpster)" count={jobStats?.vegetation_dumpster || 0} loading={loadingStats} />
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fff7ed',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Total Haul-Off</span>
                  <span style={{ fontWeight: '700', color: '#f59e0b' }}>
                    {loadingStats ? '-' : getTotalHaulOff()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* No job selected state */}
      {!currentJob && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#64748b'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <div style={{ fontSize: '16px', fontWeight: '500' }}>
            Select a job to view activity
          </div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>
            Use the dropdown above to choose a job site
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginTop: '16px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
      </div>

      {/* Closest Job Modal */}
      {showClosestJobModal && closestJob && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => {
            setShowClosestJobModal(false);
            setClosestJob(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '340px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '24px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px'
              }}>
                <MapPin size={28} color="white" />
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '20px',
                fontWeight: '700',
                margin: 0
              }}>
                Not at a Job Site
              </h2>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              <p style={{
                color: '#64748b',
                fontSize: '14px',
                textAlign: 'center',
                margin: '0 0 16px'
              }}>
                You're not within any job geofence. The closest job is:
              </p>

              <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  {closestJob.job.job_name}
                </div>
                {closestJob.job.bradanna_job_number && (
                  <div style={{
                    fontSize: '13px',
                    color: '#64748b',
                    marginBottom: '8px'
                  }}>
                    {closestJob.job.bradanna_job_number}
                  </div>
                )}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: '#dbeafe',
                  color: '#1d4ed8',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  <MapPin size={14} />
                  {formatDistance(closestJob.distance)} away
                </div>
              </div>

              <p style={{
                color: '#64748b',
                fontSize: '14px',
                textAlign: 'center',
                margin: '16px 0 0'
              }}>
                Do you want to select this job?
              </p>
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '12px',
              padding: '16px 20px',
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#f9fafb'
            }}>
              <button
                onClick={() => {
                  setShowClosestJobModal(false);
                  setClosestJob(null);
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSelectClosestJob}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Select Job
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: any;
  label: string;
  value: string;
  color: string;
}> = ({ icon: Icon, label, value, color }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '14px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  }}>
    <div style={{
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '10px'
    }}>
      <Icon size={18} color={color} />
    </div>
    <p style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: '0 0 2px' }}>
      {value}
    </p>
    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
      {label}
    </p>
  </div>
);

// Module Button Component
const ModuleButton: React.FC<{
  icon: any;
  label: string;
  color: string;
  onClick: () => void;
}> = ({ icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      padding: '14px 16px',
      backgroundColor: `${color}10`,
      border: `1px solid ${color}30`,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
  >
    <div style={{
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Icon size={18} color="white" />
    </div>
    <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
      {label}
    </span>
    <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '18px' }}>→</span>
  </button>
);

// Labor Row Component
const LaborRow: React.FC<{
  label: string;
  hours: number;
  color: string;
  loading?: boolean;
}> = ({ label, hours, color, loading }) => {
  const formatHours = (h: number) => {
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    return `${hrs}:${mins.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      padding: '14px 16px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: color
        }} />
        <span style={{ color: '#1e293b' }}>{label}</span>
      </div>
      <span style={{ fontWeight: '600', color: '#1e293b' }}>
        {loading ? '-' : formatHours(hours)}
      </span>
    </div>
  );
};

// Material Row Component
const MaterialRow: React.FC<{
  label: string;
  count: number;
  loading?: boolean;
}> = ({ label, count, loading }) => (
  <div style={{
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}>
    <span style={{ color: '#1e293b' }}>{label}</span>
    <span style={{
      fontWeight: '600',
      color: loading ? '#94a3b8' : (count > 0 ? '#1e293b' : '#94a3b8')
    }}>
      {loading ? '-' : count}
    </span>
  </div>
);
